/**
 * Threads OAuth 구현 - 모바일 앱 리다이렉트 대응
 * ChatGPT 분석에 따라 앱 내부 WebView OAuth 플로우 지원
 */

// Threads OAuth 설정
const THREADS_CONFIG = {
  CLIENT_ID: process.env.REACT_APP_THREADS_CLIENT_ID || '511936531788597',
  REDIRECT_URI: `${window.location.origin}/callback`,
  SCOPES: ['threads_basic', 'threads_content_publish', 'threads_manage_insights', 'threads_read_replies', 'threads_manage_replies'],
  AUTH_URL: 'https://www.threads.net/oauth/authorize',
  // 대안 Facebook OAuth URL (Threads는 Facebook 소유)
  FACEBOOK_AUTH_URL: 'https://www.facebook.com/dialog/oauth'
};

// ngrok URL 확인 및 로깅
console.log('🌐 현재 Origin:', window.location.origin);
console.log('🔗 Redirect URI:', THREADS_CONFIG.REDIRECT_URI);

/**
 * 랜덤 state 값 생성 (CSRF 보호)
 */
const generateState = () => {
  return Math.random().toString(36).substring(2, 15) + 
         Math.random().toString(36).substring(2, 15);
};

/**
 * 간단한 OAuth 시작 (앱 내부 WebView 플로우 지원)
 */
export const openThreadsOAuth = () => {
  return new Promise((resolve, reject) => {
    const state = generateState();
    
    // Threads OAuth URL (정규화: 호스트 고정 + scope 인코딩)
    const scopeParam = encodeURIComponent(THREADS_CONFIG.SCOPES.join(','));
    const authUrl = `${THREADS_CONFIG.AUTH_URL}?` +
      `client_id=${THREADS_CONFIG.CLIENT_ID}&` +
      `redirect_uri=${encodeURIComponent(THREADS_CONFIG.REDIRECT_URI)}&` +
      `scope=${scopeParam}&` +
      `response_type=code&` +
      `state=${state}`;

    console.log('🚀 Threads OAuth 시작 (WebView 강제):', authUrl);

    // 교환 시점 redirect_uri 일치성을 보장하기 위해 저장
    try {
      sessionStorage.setItem('oauth_redirect_uri', THREADS_CONFIG.REDIRECT_URI);
      sessionStorage.setItem('oauth_start_origin', window.location.origin);
      console.log('🔒 Stored redirect_uri for exchange:', THREADS_CONFIG.REDIRECT_URI);
    } catch (_) {}

    // 💡 ChatGPT 분석에 따른 접근:
    // 1. 모바일에서는 앱이 열림 (불가피)
    // 2. 앱 내부 WebView에서 OAuth 진행
    // 3. 완료 후 redirect_uri로 복귀

    // 인앱(WebView)에서는 시스템 브라우저로 열어 게이트/쿠키 이슈 우회
    const ua = navigator.userAgent || '';
    const isInApp = /Threads|Instagram/i.test(ua);
    console.log('🚀 Threads OAuth 시작', { isInApp, authUrl });
    if (isInApp) {
      const win = window.open(authUrl, '_blank', 'noopener');
      if (!win) {
        // 팝업 차단 fallback
        window.location.href = authUrl;
      }
    } else {
      window.location.href = authUrl;
    }
  });
};

/**
 * 인가 코드를 액세스 토큰으로 교환
 */
export const exchangeCodeForToken = async (code) => {
  try {
    console.log('🔄 토큰 교환 시작:', code);
    
    // 개발 환경에서는 모의 토큰 반환
    if (process.env.NODE_ENV === 'development') {
      console.warn('🔧 개발 모드: 모의 토큰 사용');
      await new Promise(resolve => setTimeout(resolve, 1000)); // 1초 지연
      return {
        access_token: 'mock_token_' + Date.now(),
        token_type: 'bearer',
        expires_in: 3600,
        user_id: 'mock_user_id'
      };
    }
    
    // 실제 환경에서는 백엔드 API 호출
    const response = await fetch('/api/threads/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        code: code,
        redirect_uri: THREADS_CONFIG.REDIRECT_URI
      })
    });

    if (!response.ok) {
      throw new Error(`토큰 교환 실패: ${response.status}`);
    }

    const data = await response.json();
    console.log('✅ 토큰 획득 완료');
    return data;
  } catch (error) {
    console.error('❌ 토큰 교환 오류:', error);
    throw error;
  }
};

/**
 * 전체 Threads 로그인 프로세스
 */
export const handleThreadsLogin = async () => {
  try {
    console.log('🔐 Threads 로그인 시작');
    const { errorLogger } = await import('./errorLogger');
    errorLogger.startTrace({ flow: 'login_popup' });
    errorLogger.trace('auth_start');
    
    // URL에 인가 코드가 있는지 확인 (리다이렉트 복귀 시)
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const error = urlParams.get('error');

    if (error) {
      await errorLogger.flushOnError({ component: 'threadsOAuth', event: 'oauth_error', status: 0, endpoint: 'oauth', extra: { error } });
      throw new Error(`OAuth Error: ${error}`);
    }

    if (code && state) {
      // 일반 로그인: 코드 교환은 /callback에서만 수행하도록 위임
      console.log('✅ 인가 코드 발견: 콜백으로 위임');
      const search = urlParams.toString();
      if (window.location.pathname !== '/callback') {
        window.location.replace(`/callback?${search}`);
      }
      return null;
    }

    // 인가 코드가 없음 - OAuth 시작
    await openThreadsOAuth();
    
    // 이 시점에서는 리다이렉트되므로 여기까지 오지 않음
    return null;
  } catch (error) {
    console.error('❌ Threads 로그인 실패:', error);
    try {
      const { errorLogger } = await import('./errorLogger');
      await errorLogger.flushOnError({
        component: 'threadsOAuth',
        event: 'login_failed',
        status: error?.response?.status,
        endpoint: 'oauth',
        stack: error?.stack
      });
    } catch (_) {}
    throw error;
  }
};

/**
 * 로그아웃 처리
 */
export const handleThreadsLogout = () => {
  localStorage.removeItem('threads_token');
  localStorage.removeItem('threads_login_time');
  localStorage.removeItem('oauth_state');
  console.log('✅ Threads 로그아웃 완료');
};

/**
 * 현재 로그인 상태 확인
 */
export const isThreadsLoggedIn = () => {
  const token = localStorage.getItem('threads_token');
  return !!token;
};

/**
 * 저장된 토큰 정보 가져오기
 */
export const getThreadsToken = () => {
  const tokenData = localStorage.getItem('threads_token');
  return tokenData ? JSON.parse(tokenData) : null;
};